import React from 'react';
import { createPortal } from 'react-dom';
import { buttonClass, theme } from '../../../ui/theme';

export default function SettingsPanel({
  showSettings,
  settingsPanelRef,
  settingsButtonRef,
  setShowSettings,
  t,
  i18n,
  fadeInOut,
  setFadeInOut,
  useWasmOpusDecode,
  setUseWasmOpusDecode,
  stabilityMode,
  setStabilityMode,
  wasmSimdSupported,
  surroundEnabled,
  setSurroundEnabled,
  spaceModeEnabled,
  setSpaceModeEnabled,
  bassEnabled,
  setBassEnabled,
  playlistLoop,
  setPlaylistLoop,
  sleepTimer,
  sleepTimerMinutes,
  setSleepTimerHandler,
  isMobileDevice,
  lyricSettings,
  setLyricSettings,
  formatTime
}) {
  // 根据语言获取分钟单位
  const getMinutesUnit = () => {
    const lang = i18n?.language || 'ja';
    if (lang === 'cn' || lang === 'tw') return '分';
    if (lang === 'en') return 'min';
    return t('player.minutes'); // 其他语言使用翻译
  };
  
  // 根据语言获取标签字体大小（日语环境使用更小字体）
  const getLabelFontSize = () => {
    const lang = i18n?.language || 'ja';
    return lang === 'ja' ? 'text-[10px]' : 'text-xs';
  };
  
  const minutesUnit = getMinutesUnit();
  const labelFontSize = getLabelFontSize();
  const simdStatusLabel = React.useMemo(() => {
    if (wasmSimdSupported === null) return t('player.wasmSimdChecking');
    return wasmSimdSupported ? t('player.wasmSimdEnabled') : t('player.wasmSimdNotSupported');
  }, [t, wasmSimdSupported]);
  const [activeTab, setActiveTab] = React.useState('basic'); // 'basic' | 'surround'
  const [position, setPosition] = React.useState({ top: 0, left: 0, width: 0, height: 0 });
  const [translationDropdownOpen, setTranslationDropdownOpen] = React.useState({
    zh: false,
    ja: false,
    en: false,
    other: false
  });
  const translationDropdownRefZh = React.useRef(null);
  const translationDropdownRefJa = React.useRef(null);
  const translationDropdownRefEn = React.useRef(null);
  const translationDropdownRefOther = React.useRef(null);
  const translationDropdownRefs = React.useMemo(
    () => ({
      zh: translationDropdownRefZh,
      ja: translationDropdownRefJa,
      en: translationDropdownRefEn,
      other: translationDropdownRefOther
    }),
    []
  );

  // 点击外部关闭翻译语言下拉菜单
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      const keys = ['zh', 'ja', 'en', 'other'];
      keys.forEach(key => {
        if (translationDropdownOpen[key]) {
          const ref = translationDropdownRefs[key].current;
          if (ref && !ref.contains(e.target)) {
            setTranslationDropdownOpen(prev => ({ ...prev, [key]: false }));
          }
        }
      });
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setTranslationDropdownOpen({ zh: false, ja: false, en: false, other: false });
      }
    };
    const hasOpen = Object.values(translationDropdownOpen).some(v => v);
    if (hasOpen) {
      // 使用捕获阶段确保在其他事件处理器之前执行
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [translationDropdownOpen, translationDropdownRefs]);

  // 计算设置按钮位置（使用节流避免频繁 DOM 读取）
  React.useEffect(() => {
    if (!showSettings || !settingsButtonRef?.current) return;

    let rafId = null;
    let pendingUpdate = false;

    const updatePosition = () => {
      if (pendingUpdate) return;
      pendingUpdate = true;

      rafId = requestAnimationFrame(() => {
        pendingUpdate = false;
        const button = settingsButtonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        setPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showSettings, settingsButtonRef]);

  // 移动端设置面板打开时：锁定背景页面滚动（避免上下滑动穿透到网页）
  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const isMobile = !!(isMobileDevice && isMobileDevice());
    const body = document.body;
    const html = document.documentElement;
    if (!body || !html) return;

    const LOCK_KEY = 'tenryuScrollLock';
    const PREV_KEY = 'tenryuScrollLockPrev';
    const Y_KEY = 'tenryuScrollLockY';

    const lock = () => {
      // 已锁定：不要重复覆盖“原始样式”，否则会导致解锁后仍保持 locked（你现在遇到的“永久失去焦点/不能滚动”）
      if (body.dataset && body.dataset[LOCK_KEY] === '1') return;

      const scrollY = window.scrollY || window.pageYOffset || 0;
      try {
        body.dataset[LOCK_KEY] = '1';
        body.dataset[Y_KEY] = String(scrollY);
        body.dataset[PREV_KEY] = JSON.stringify({
          bodyPosition: body.style.position,
          bodyTop: body.style.top,
          bodyLeft: body.style.left,
          bodyRight: body.style.right,
          bodyWidth: body.style.width,
          bodyOverflow: body.style.overflow,
          htmlOverflow: html.style.overflow
        });
      } catch (_) {}

      // 使用 position: fixed 保留视觉位置；overflow hidden 防止滚动
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
    };

    const unlock = () => {
      try {
        if (!body.dataset || body.dataset[LOCK_KEY] !== '1') return;
      } catch (_) {
        return;
      }

      let prev = null;
      let scrollY = 0;
      try {
        prev = body.dataset[PREV_KEY] ? JSON.parse(body.dataset[PREV_KEY]) : null;
      } catch (_) {
        prev = null;
      }
      try {
        scrollY = Number(body.dataset[Y_KEY] || 0) || 0;
      } catch (_) {
        scrollY = 0;
      }

      if (prev) {
        body.style.position = prev.bodyPosition || '';
        body.style.top = prev.bodyTop || '';
        body.style.left = prev.bodyLeft || '';
        body.style.right = prev.bodyRight || '';
        body.style.width = prev.bodyWidth || '';
        body.style.overflow = prev.bodyOverflow || '';
        html.style.overflow = prev.htmlOverflow || '';
      } else {
        // 兜底：至少确保可滚动
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.overflow = '';
        html.style.overflow = '';
      }

      try { delete body.dataset[LOCK_KEY]; } catch (_) {}
      try { delete body.dataset[PREV_KEY]; } catch (_) {}
      try { delete body.dataset[Y_KEY]; } catch (_) {}

      try { window.scrollTo(0, scrollY); } catch (_) {}
    };

    // showSettings=false：如果之前锁过但没解锁（异常/热更新/重渲染），这里也会强制解锁
    if (!showSettings || !isMobile) {
      unlock();
      return;
    }

    lock();
    return () => unlock();
  }, [showSettings, isMobileDevice]);

  if (!showSettings || typeof document === 'undefined') return null;

  // 计算面板位置
  const panelWidth = Math.max(320, Math.min(528, window.innerWidth - 32));
  const isMobile = isMobileDevice();

  const panelStyle = isMobile
    ? {
        bottom: 'calc(var(--tenryu-player-h, 0px) + 88px)',
        left: '16px',
        right: '16px',
        width: 'auto',
        maxWidth: 'none',
        maxHeight: 'calc(100vh - var(--tenryu-player-h, 0px) - 88px - 24px)'
      }
    : (() => {
        const estimatedPanelHeight = 400;
        const spaceAbove = position.top;
        const spaceBelow = window.innerHeight - position.top - position.height;
        const showAbove = spaceAbove >= estimatedPanelHeight || spaceAbove > spaceBelow;
        return showAbove
          ? {
              bottom: `${window.innerHeight - position.top + 8}px`,
              left: `${Math.max(16, Math.min(position.left, window.innerWidth - panelWidth - 16))}px`,
              width: `${panelWidth}px`,
              maxWidth: 'calc(100vw - 32px)'
            }
          : {
              top: `${position.top + position.height + 8}px`,
              left: `${Math.max(16, Math.min(position.left, window.innerWidth - panelWidth - 16))}px`,
              width: `${panelWidth}px`,
              maxWidth: 'calc(100vw - 32px)'
            };
      })();

  const panelContent = (
    <div
      ref={settingsPanelRef}
      className={[
        // 顶部不再用 py-8，避免把标题往下推；内容区由 header 自带 padding 控制
        'fixed text-white pt-0 px-3 pb-6 rounded-t-lg rounded-b-lg shadow-2xl h-[448px] min-h-[448px] overflow-y-auto overscroll-contain tenryu-player-scroll-area z-[10000]',
        theme.surfaces.modalPanel
      ].join(' ')}
      style={panelStyle}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-no-toggle
    >
      <div className="space-y-3 text-sm pb-6">
        {/* 头部：贴到面板顶端（sticky），标题区在最上方 */}
        <div className="sticky top-0 z-10 -mx-3 px-3 pt-3 pb-2 bg-black/0 backdrop-blur-sm">
          <div className="relative">
            <h3 className="font-semibold text-base">{t('player.settings')}</h3>
            <button
              type="button"
              aria-label={t('player.closeSettings') || '关闭设置'}
              title={t('player.closeSettings') || '关闭设置'}
              className={[
                // 右上角，距边沿 8px
                'absolute top-0 right-0',
                // 再小一号
                'h-5 w-5 rounded-full',
                'text-white font-semibold leading-none',
                'flex items-center justify-center',
                'bg-gradient-to-br from-emerald-400 to-emerald-600',
                'shadow-md shadow-emerald-500/20',
                'border border-white/20',
                'hover:from-emerald-300 hover:to-emerald-600',
                'active:scale-[0.98] transition-all'
              ].join(' ')}
              onClick={() => {
                try { setShowSettings?.(false); } catch (_) {}
              }}
              data-no-toggle
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex border-b border-white/10 mb-2 gap-0">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={[
              'px-3 py-1.5 font-medium rounded-t transition-colors',
              'text-[14px]',
              activeTab === 'basic'
                ? 'bg-white/10 text-white border-b-2 border-emerald-500 -mb-px'
                : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
            ].join(' ')}
          >
            {t('player.settingsTabBasic')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('surround')}
            className={[
              'px-3 py-1.5 font-medium rounded-t transition-colors',
              'text-[14px]',
              activeTab === 'surround'
                ? 'bg-white/10 text-white border-b-2 border-emerald-500 -mb-px'
                : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
            ].join(' ')}
          >
            {t('player.settingsTabSurround')}
          </button>
        </div>

        {activeTab === 'surround' && (
        <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.fadeInOut')}</div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={fadeInOut}
              onChange={(e) => setFadeInOut(e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            <span className={`ml-2 ${labelFontSize} text-white/55`}>{t('player.fadeInOutDesc')}</span>
          </div>
        </div>

        <hr className="border-white/10 my-2" />

        <p className={`${labelFontSize} text-white/70 whitespace-pre-line text-center`}>
          {t('player.panoramicSoundHint')}
        </p>

        <div className="flex justify-center my-2">
          <button
            type="button"
            onClick={() => {
              const lang = (i18n && i18n.language) || (typeof localStorage !== 'undefined' && localStorage.getItem('i18nextLng')) || 'ja';
              const path = lang === 'ja' ? '/tuac-pro' : `/${lang}/tuac-pro`;
              window.history.pushState({}, '', path);
              window.dispatchEvent(new Event('app:navigate'));
            }}
            className={buttonClass({
              variant: 'outline',
              size: 'sm',
              className: 'text-xs !bg-gradient-to-r !from-blue-800 !to-blue-700 hover:!from-blue-700 hover:!to-blue-600 !border-blue-600/50 text-white'
            })}
          >
            {t('player.tuacProExperience')}
          </button>
        </div>

        <hr className="border-white/10 my-2" />

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.direct')}</div>
          <div className="flex-1 flex items-center justify-start">
            <button
              type="button"
              onClick={() => setSurroundEnabled(!surroundEnabled)}
              className={buttonClass({
                variant: surroundEnabled ? 'outline' : 'primary',
                size: 'sm',
                className: `text-xs px-3 py-1.5 ${surroundEnabled ? 'bg-red-900/40 border-red-500/50 text-white/90 hover:bg-red-900/60' : ''}`
              })}
            >
              {surroundEnabled ? t('player.directOffProcessing') : t('player.directOn')}
            </button>
          </div>
        </div>

        {surroundEnabled && (
          <>
            <div className="flex items-center gap-3">
              <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.spaceExpand')}</div>
              <div className="flex-1 flex items-center justify-start">
                <input
                  type="checkbox"
                  checked={!!spaceModeEnabled}
                  onChange={(e) => setSpaceModeEnabled(e.target.checked)}
                  className="h-4 w-4 accent-emerald-500"
                />
                <span className={`ml-2 ${labelFontSize} text-white/55`}>{t('player.spaceExpandDesc')}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.virtualBass')}</div>
              <div className="flex-1 flex items-center justify-start">
                <input
                  type="checkbox"
                  checked={!!bassEnabled}
                  onChange={(e) => setBassEnabled(e.target.checked)}
                  className="h-4 w-4 accent-emerald-500"
                />
                <span className={`ml-2 ${labelFontSize} text-white/55`}>{t('player.virtualBassDesc')}</span>
              </div>
            </div>
          </>
        )}

        </div>
        )}

        {activeTab === 'basic' && (
        <div className="space-y-3">
        {wasmSimdSupported !== false && (
          <>
            <div className="flex items-center gap-3">
              <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.wasmSimdStatus')}</div>
              <div className="flex-1 flex items-center justify-start">
                <span className={`${labelFontSize} text-white/55`}>{simdStatusLabel}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.wasmOpusDecode')}</div>
              <div className="flex-1 flex items-center justify-start">
                <input
                  type="checkbox"
                  checked={!!useWasmOpusDecode}
                  onChange={(e) => setUseWasmOpusDecode(e.target.checked)}
                  disabled={wasmSimdSupported === null}
                  className="h-4 w-4 accent-emerald-500 disabled:opacity-40"
                />
                <span className={`ml-2 ${labelFontSize} text-white/55`}>
                  {wasmSimdSupported === null ? t('player.wasmSimdChecking') : t('player.wasmOpusDecodeDesc')}
                </span>
              </div>
            </div>

          </>
        )}

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>
            {t('player.stabilityMode') || '稳定播放'}
          </div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={!!stabilityMode}
              onChange={(e) => setStabilityMode && setStabilityMode(e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            <span className={`ml-2 ${labelFontSize} text-white/55`}>
              {t('player.stabilityModeDesc') || '增大缓冲，减少卡顿/爆音（延迟略增）'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.playlistLoop')}</div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={playlistLoop}
              onChange={(e) => setPlaylistLoop(e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            <span className={`ml-2 ${labelFontSize} text-white/55`}>{t('player.playlistLoopDesc')}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.showTranslation')}</div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={lyricSettings?.showTranslation ?? true}
              onChange={(e) => {
                setLyricSettings(prev => ({
                  ...prev,
                  showTranslation: e.target.checked
                }));
              }}
              className="h-4 w-4 accent-emerald-500"
            />
          </div>
        </div>

        {lyricSettings?.showTranslation && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {/* 中文歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.chineseTo') || '中文 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.zh} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, zh: !prev.zh }))}
                      className={theme.dropdown.triggerCompact}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.zh === 'ja' ? t('player.japanese') || '日语' :
                         lyricSettings?.translationMap?.zh === 'en' ? t('player.english') || '英语' :
                         t('player.japanese') || '日语'}
                      </span>
                    </button>
                    {translationDropdownOpen.zh && (
                      <div
                        className={[theme.dropdown.menu, theme.dropdown.menuUp].join(' ')}
                        role="listbox"
                      >
                        {[
                          { value: 'ja', label: t('player.japanese') || '日语' },
                          { value: 'en', label: t('player.english') || '英语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.zh === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  zh: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, zh: false }));
                            }}
                            className={[
                              theme.dropdown.optionCompact,
                              lyricSettings?.translationMap?.zh === opt.value ? theme.dropdown.optionCompactSelected : ''
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 日语歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.japaneseTo') || '日语 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.ja} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, ja: !prev.ja }))}
                      className={theme.dropdown.triggerCompact}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.ja === 'zh' ? t('player.chinese') || '中文' :
                         lyricSettings?.translationMap?.ja === 'en' ? t('player.english') || '英语' :
                         t('player.chinese') || '中文'}
                      </span>
                    </button>
                    {translationDropdownOpen.ja && (
                      <div
                        className={[theme.dropdown.menu, theme.dropdown.menuUp].join(' ')}
                        role="listbox"
                      >
                        {[
                          { value: 'zh', label: t('player.chinese') || '中文' },
                          { value: 'en', label: t('player.english') || '英语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.ja === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  ja: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, ja: false }));
                            }}
                            className={[
                              theme.dropdown.optionCompact,
                              lyricSettings?.translationMap?.ja === opt.value ? theme.dropdown.optionCompactSelected : ''
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 英语歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.englishTo') || '英语 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.en} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, en: !prev.en }))}
                      className={theme.dropdown.triggerCompact}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.en === 'zh' ? t('player.chinese') || '中文' :
                         lyricSettings?.translationMap?.en === 'ja' ? t('player.japanese') || '日语' :
                         t('player.chinese') || '中文'}
                      </span>
                    </button>
                    {translationDropdownOpen.en && (
                      <div
                        className={[theme.dropdown.menu, theme.dropdown.menuUp].join(' ')}
                        role="listbox"
                      >
                        {[
                          { value: 'zh', label: t('player.chinese') || '中文' },
                          { value: 'ja', label: t('player.japanese') || '日语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.en === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  en: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, en: false }));
                            }}
                            className={[
                              theme.dropdown.optionCompact,
                              lyricSettings?.translationMap?.en === opt.value ? theme.dropdown.optionCompactSelected : ''
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 其他歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.otherTo') || '其他 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.other} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, other: !prev.other }))}
                      className={theme.dropdown.triggerCompact}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.other === 'zh' ? t('player.chinese') || '中文' :
                         lyricSettings?.translationMap?.other === 'ja' ? t('player.japanese') || '日语' :
                         lyricSettings?.translationMap?.other === 'en' ? t('player.english') || '英语' :
                         t('player.chinese') || '中文'}
                      </span>
                    </button>
                    {translationDropdownOpen.other && (
                      <div
                        className={[theme.dropdown.menu, theme.dropdown.menuUp].join(' ')}
                        role="listbox"
                      >
                        {[
                          { value: 'zh', label: t('player.chinese') || '中文' },
                          { value: 'ja', label: t('player.japanese') || '日语' },
                          { value: 'en', label: t('player.english') || '英语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.other === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  other: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, other: false }));
                            }}
                            className={[
                              theme.dropdown.optionCompact,
                              lyricSettings?.translationMap?.other === opt.value ? theme.dropdown.optionCompactSelected : ''
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* 字幕样式设置 */}
        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.lyricFontSize')}</div>
          <div className="flex-1 flex justify-start items-center gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="10"
                max="32"
                step="1"
                value={lyricSettings?.fontSize ?? 16}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 10 && value <= 32) {
                    setLyricSettings(prev => ({
                      ...prev,
                      fontSize: value
                    }));
                  }
                }}
                className={['text-xs px-2 py-1 w-20', theme.inputs.base].join(' ')}
              />
              <span className={`${labelFontSize} text-white/55`}>px</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0 flex items-center gap-2`}>
            {t('player.sleepTimer')}
            <span className={`${labelFontSize} text-white/55`}>
              ({sleepTimerMinutes > 0 ? `${sleepTimerMinutes} ${minutesUnit}` : t('player.notSet')})
            </span>
          </div>
          <div className="flex-1 flex justify-start">
            <div className="flex gap-0.5 flex-wrap justify-start">
              {[15, 30, 45, 60, 90, 120].map(minutes => (
                <button
                  key={minutes}
                  onClick={() => setSleepTimerHandler(minutes)}
                  className={buttonClass({
                    variant: sleepTimerMinutes === minutes ? 'primary' : 'outline',
                    size: 'xs',
                    className: `text-[10px] px-1 py-0.5 h-5 leading-tight ${sleepTimerMinutes === minutes ? '' : 'bg-white/[0.08] border-gray-400/30 text-white/70 hover:bg-white/[0.12] hover:border-gray-400/40'}`
                  })}
                >
                  {minutes}{minutesUnit}
                </button>
              ))}
              {sleepTimer && (
                <button
                  onClick={() => setSleepTimerHandler(0)}
                  className={buttonClass({ 
                    variant: 'danger', 
                    size: 'xs',
                    className: 'text-[10px] px-1 py-0.5 h-5 leading-tight'
                  })}
                >
                  取消
                </button>
              )}
            </div>
          </div>
        </div>

        </div>
        )}
      </div>
    </div>
  );

  return createPortal(panelContent, document.body);
}

